import { NavLink, Outlet, useLocation, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Shield, Radio, Eye, User, UserCircle, BellRinging, DownloadSimple, CheckCircle, Users, Coins, ShoppingCart, Heart, HandHeart, Sparkle, FilmStrip, IdentificationCard, BookOpenText, MagnifyingGlass, GearSix, Sun, QrCode } from '@phosphor-icons/react';
import ThemePicker from '@/components/ThemePicker';
import { getRole } from '@/components/RoleGate';
import { NAV } from '@/constants/testIds/analysis';
import { useInstall } from '@/lib/install';
import ModelPicker from '@/components/ModelPicker';
import CreditsChip from '@/components/CreditsChip';

const links = [
  { to: '/', label: 'Analyze', icon: Shield, id: NAV.home, end: true },
  { to: '/live', label: 'Live', icon: Radio, id: NAV.live },
  { to: '/autoguard', label: 'Auto', icon: BellRinging, id: 'nav-autoguard' },
  { to: '/body', label: 'Body', icon: User, id: 'nav-body' },
  { to: '/video', label: 'Video', icon: FilmStrip, id: 'nav-video' },
  { to: '/truth', label: 'Truth', icon: MagnifyingGlass, id: 'nav-truth' },
  { to: '/enlightenment', label: 'Enlighten', icon: Sun, id: 'nav-enlightenment' },
  { to: '/settings', label: 'Settings', icon: GearSix, id: 'nav-settings' },
  { to: '/profile', label: 'Profile', icon: IdentificationCard, id: 'nav-profile' },
  { to: '/couples', label: 'Couples', icon: Heart, id: 'nav-couples' },
  { to: '/guardian', label: 'Circle', icon: Users, id: 'nav-guardian' },
  { to: '/shop', label: 'Shop', icon: ShoppingCart, id: 'nav-shop' },
  { to: '/tactics', label: 'Tactics', icon: BookOpenText, id: 'nav-tactics' },
  { to: '/family', label: 'Family', icon: HandHeart, id: 'nav-family' },
  { to: '/link', label: 'Link', icon: QrCode, id: 'nav-link' },
  { to: '/credits', label: 'Credits', icon: Coins, id: 'nav-credits', mobileHidden: true },
];

export default function Layout() {
  const loc = useLocation();
  const { installed, canInstall, install } = useInstall();
  const [growthActive, setGrowthActive] = useState(false);

  useEffect(() => {
    try {
      const g = JSON.parse(localStorage.getItem('unmanip_gate') || 'null');
      setGrowthActive(g?.verdict === 'growth_path');
    } catch {}
  }, [loc.pathname]);

  const dynamicLinks = [
    ...links.filter(l => !(l.to === '/tactics' && getRole() === 'child')),
    ...(growthActive ? [{ to: '/growth', label: 'Growth', icon: Sparkle, id: 'nav-growth' }] : []),
  ];

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100 font-mono">
      {/* Grain overlay */}
      <div aria-hidden className="pointer-events-none fixed inset-0 z-0 opacity-[0.035] mix-blend-overlay"
        style={{ backgroundImage: "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='140' height='140'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.9'/></svg>\")" }} />

      <header className="sticky top-0 z-20 border-b border-zinc-800 bg-zinc-950/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 md:px-6 md:py-4 md:pt-6">
          <div className="flex min-w-0 flex-1 items-center gap-3 md:flex-none">
            <div className="relative flex h-8 w-8 shrink-0 items-center justify-center border border-zinc-700 bg-zinc-900">
              <Eye weight="fill" className="h-4 w-4 text-emerald-400" />
              <span className="absolute -right-0.5 -top-0.5 h-1.5 w-1.5 animate-pulse bg-emerald-400" />
            </div>
            <div className="flex min-w-0 flex-col leading-none">
              <span className="font-sans text-lg font-black tracking-tighter">unmanipulated</span>
              <span className="text-[10px] uppercase tracking-[0.24em] text-zinc-500">personal analyst</span>
            </div>
            <span className="hidden md:inline-flex ml-3 items-center gap-1.5 border border-emerald-500/40 bg-emerald-500/5 px-2 py-1 text-[10px] uppercase tracking-[0.22em] text-emerald-400">
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inset-0 rounded-full bg-emerald-400 animate-ping opacity-70" />
                <span className="relative rounded-full bg-emerald-400 h-1.5 w-1.5" />
              </span>
              guardian · active
            </span>
          </div>
          <div className="flex shrink-0 flex-col items-end leading-tight text-right md:absolute md:right-6 md:top-1" data-testid="production-credit">
            <span data-testid="header-tagline" className="font-sans text-[11px] sm:text-xs font-black uppercase tracking-[0.3em] text-emerald-400 whitespace-nowrap [text-shadow:0_0_12px_rgba(57,255,20,.55)]">PROJECT FREEDOM</span>
          </div>
          <div className="flex w-full flex-wrap items-center justify-between gap-2 md:hidden">
            {installed ? (
              <span data-testid="header-installed"
                className="inline-flex items-center gap-1.5 border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-400">
                <CheckCircle weight="fill" className="h-3.5 w-3.5" /> installed
              </span>
            ) : canInstall ? (
              <button data-testid="header-install-btn" onClick={install}
                className="inline-flex items-center gap-1.5 border border-emerald-500 bg-emerald-500 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-950 hover:bg-emerald-400">
                <DownloadSimple weight="bold" className="h-3.5 w-3.5" /> install
              </button>
            ) : (
              <Link to="/install" data-testid="header-install-link"
                className="inline-flex items-center gap-1.5 border border-emerald-500 bg-emerald-500 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-950 hover:bg-emerald-400">
                <DownloadSimple weight="bold" className="h-3.5 w-3.5" /> install
              </Link>
            )}
            <div className="flex items-center gap-2">
              <CreditsChip />
              <Link to="/account" data-testid="header-account-link-mobile" title="Account"
                className="inline-flex items-center border border-zinc-800 px-2 py-1.5 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400">
                <UserCircle weight="fill" className="h-4 w-4" />
              </Link>
              <ThemePicker />
              <ModelPicker />
            </div>
          </div>
          <nav data-testid="main-nav" className="grid w-full grid-cols-4 gap-1 md:ml-auto md:flex md:w-auto md:flex-wrap md:items-center md:justify-end">
            {dynamicLinks.map(({ to, label, icon: Icon, id, end, mobileHidden }) => (
              <NavLink
                key={to}
                to={to}
                end={end}
                data-testid={id}
                className={({ isActive }) =>
                  `group ${mobileHidden ? 'hidden md:flex' : 'flex'} flex-col items-center justify-center gap-1 border px-1 py-1.5 text-[9px] uppercase tracking-[0.12em] transition-colors duration-200 md:flex-row md:gap-2 md:px-3 md:py-2 md:text-xs md:tracking-[0.18em] ${
                    isActive
                      ? 'border-zinc-100 bg-zinc-100 text-zinc-950'
                      : 'border-zinc-800/80 text-zinc-400 hover:border-zinc-700 hover:text-zinc-100 md:border-transparent'
                  }`
                }
              >
                <Icon weight="bold" className="h-4 w-4 md:h-3.5 md:w-3.5" />
                {label}
              </NavLink>
            ))}
            <div className="hidden md:contents">
              {installed ? (
                <span className="ml-2 inline-flex items-center gap-1.5 border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-400">
                  <CheckCircle weight="fill" className="h-3.5 w-3.5" /> installed
                </span>
              ) : canInstall ? (
                <button onClick={install}
                  className="ml-2 inline-flex items-center gap-1.5 border border-emerald-500 bg-emerald-500 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-950 hover:bg-emerald-400">
                  <DownloadSimple weight="bold" className="h-3.5 w-3.5" /> install
                </button>
              ) : (
                <Link to="/install"
                  className="ml-2 inline-flex items-center gap-1.5 border border-emerald-500 bg-emerald-500 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold text-zinc-950 hover:bg-emerald-400">
                  <DownloadSimple weight="bold" className="h-3.5 w-3.5" /> install
                </Link>
              )}
              <CreditsChip />
              <Link to="/account" data-testid="header-account-link" title="Account"
                className="inline-flex items-center border border-zinc-800 px-2 py-1.5 text-zinc-400 hover:border-emerald-500 hover:text-emerald-400">
                <UserCircle weight="fill" className="h-4 w-4" />
              </Link>
              <ThemePicker />
              <ModelPicker />
            </div>
          </nav>
        </div>
      </header>

      <main key={loc.pathname} className="relative z-10 mx-auto max-w-7xl px-4 py-6 md:px-6 md:py-10 animate-[fade-in_.35s_ease-out]">
        <Outlet />
      </main>

      <footer className="relative z-10 border-t border-zinc-900 px-4 py-6 md:px-6">
        <div className="mx-auto max-w-7xl flex flex-wrap items-center justify-between gap-3">
          <span className="text-[10px] uppercase tracking-[0.28em] text-zinc-600">
            unmanipulated · © a Lyin' Eyes production
          </span>
          <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 border border-emerald-500/30 bg-emerald-500/5 px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] text-emerald-400">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
              burn after reading · nothing stored
            </span>
            <span className="inline-flex items-center gap-2 border border-zinc-800 px-3 py-1.5 text-[10px] uppercase tracking-[0.24em] text-zinc-400">
              never sold · never surrendered
            </span>
          </div>
        </div>
        <div className="mx-auto max-w-7xl mt-3 flex flex-wrap items-center gap-4 text-[10px] uppercase tracking-[0.24em] text-zinc-600">
          <Link to="/legal" className="hover:text-emerald-400 transition-colors">terms &amp; waiver</Link>
          <span className="text-zinc-800">·</span>
          <Link to="/family" data-testid="footer-family-link" className="hover:text-emerald-400 transition-colors">special features · parents only</Link>
        </div>
      </footer>
    </div>
  );
}
